import React from 'react';
import { X, Calendar, Clock, TrendingUp, User, Settings, LogOut } from 'lucide-react';
import { Card, Badge } from '../design-system';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '../LanguageSwitcher';

export const RightDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <aside
        className={`
          fixed lg:sticky top-0 right-0 z-50
          h-screen w-80 bg-gray-50 border-l border-gray-200
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('navigation.profile')}</h2>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
            {/* User Profile Section */}
            <Card padding="default">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={() => {
                    navigate('/profile');
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <User className="h-4 w-4" />
                  {t('navigation.profile')}
                </button>
                
                <button
                  onClick={() => {
                    navigate('/settings');
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  {t('navigation.settings')}
                </button>
                
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('auth.logout')}
                  </button>
                </div>
              </div>
            </Card>

            {/* Language Switcher Section */}
            <Card padding="default">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('language.selectLanguage')}</h3>
              <div className="flex justify-center">
                <LanguageSwitcher />
              </div>
            </Card>
            {/* Quick Stats */}
            <Card padding="sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('rightDrawer.quickStats')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('rightDrawer.activeUsers')}</span>
                  <Badge variant="success">1,234</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('rightDrawer.totalItems')}</span>
                  <Badge variant="primary">5,678</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('rightDrawer.pending')}</span>
                  <Badge variant="warning">42</Badge>
                </div>
              </div>
            </Card>

            {/* Recent Activity */}
            <Card padding="sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('rightDrawer.recentActivity')}</h3>
              <div className="space-y-3">
                <ActivityItem
                  icon={<TrendingUp className="h-4 w-4 text-green-600" />}
                  title={t('rightDrawer.activity.newUser')}
                  time={t('rightDrawer.time.minutesAgo', { count: 2 })}
                />
                <ActivityItem
                  icon={<Calendar className="h-4 w-4 text-blue-600" />}
                  title={t('rightDrawer.activity.eventScheduled')}
                  time={t('rightDrawer.time.minutesAgo', { count: 15 })}
                />
                <ActivityItem
                  icon={<Clock className="h-4 w-4 text-orange-600" />}
                  title={t('rightDrawer.activity.taskCompleted')}
                  time={t('rightDrawer.time.hoursAgo', { count: 1 })}
                />
                <ActivityItem
                  icon={<TrendingUp className="h-4 w-4 text-green-600" />}
                  title={t('rightDrawer.activity.reportGenerated')}
                  time={t('rightDrawer.time.hoursAgo', { count: 2 })}
                />
              </div>
            </Card>

            {/* Upcoming Events */}
            <Card padding="sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('rightDrawer.upcoming')}</h3>
              <div className="space-y-3">
                <EventItem
                  title={t('rightDrawer.events.teamMeeting')}
                  date={t('rightDrawer.events.today', { time: '3:00 PM' })}
                  color="bg-blue-500"
                />
                <EventItem
                  title={t('rightDrawer.events.projectReview')}
                  date={t('rightDrawer.events.tomorrow', { time: '10:00 AM' })}
                  color="bg-purple-500"
                />
                <EventItem
                  title={t('rightDrawer.events.clientCall')}
                  date={t('rightDrawer.events.friday', { time: '2:00 PM' })}
                  color="bg-green-500"
                />
              </div>
            </Card>
          </div>
        </div>
      </aside>
    </>
  );
};

const ActivityItem = ({ icon, title, time }) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-gray-900 truncate">{title}</p>
      <p className="text-xs text-gray-500">{time}</p>
    </div>
  </div>
);

const EventItem = ({ title, date, color }) => (
  <div className="flex items-center gap-3">
    <div className={`h-2 w-2 rounded-full flex-shrink-0 ${color}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-gray-900 truncate">{title}</p>
      <p className="text-xs text-gray-500">{date}</p>
    </div>
  </div>
);
